import { Action, createReducer, on } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';

import { TopicActions } from './topic.actions';
import { TopicState } from './topic.state';
import { DEFAULT_COLUMN, DEFAULT_ROW_HEIGHT } from '../../features/market-comparison/constants/default-column';
import { Column } from '../../features/market-comparison/models/column.model';
import { arrayToFixedValueRecord } from '../../features/shared/utils/array-to-record';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { Datapoint, loadingPlaceholder } from '../../features/unified-datapoint/models/datapoint.model';
import { MarketComparisonActions } from '../market-comparison/market-comparison.actions';

export const initialState: TopicState = {
  list: {},
  loadingColumns: {},
  columns: [DEFAULT_COLUMN],
  submitting: false,
  rowHeight: DEFAULT_ROW_HEIGHT,
  frequentEntryHightlights: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(TopicActions.Clear, (state, action): TopicState => ({ ...initialState })),
    on(TopicActions.ClearList, (state, action): TopicState => ({ ...state, list: {} })),
    on(
      TopicActions.LoadTopic,
      (state, action): TopicState => ({
        ...state,
        error: undefined,
        columns: action.columns || state.columns,
        topicId: action.topicId,
        list: {},
        loadingColumns: action.columns ? arrayToFixedValueRecord<Column, boolean>(action.columns, true, 'id') : {},
      })
    ),
    on(
      TopicActions.LoadTopicValues,
      (state, action): TopicState => ({
        ...state,
        error: undefined,
        loadingColumns: { ...state.loadingColumns, ...action.loadingColumns },
      })
    ),
    on(
      TopicActions.LoadTopicValuesSuccess,
      (state, action): TopicState => ({
        ...state,
        error: undefined,
        list: { ...state.list, ...action.list },
        loadingColumns: { ...state.loadingColumns, ...action.finishedColumns },
      })
    ),
    on(
      MarketComparisonActions.UpdateDatapointSuccess,
      (state, action): TopicState => ({
        ...state,
        list: {
          ...state.list,
          [action.event.columnId]: {
            ...state.list[action.event.columnId],
            [action.event.transactionId]: action.datapoint,
          },
        },
      })
    ),
    on(MarketComparisonActions.UpdateDatapointsSuccess, (state, action): TopicState => {
      if (!action?.datapoints?.length) {
        return state;
      }

      const list = action.datapoints.reduce((acc, datapoint) => {
        acc[action.columnId] = acc[action.columnId] || {};
        acc[action.columnId][datapoint.libraryTransactionId] = datapoint;
        return acc;
      }, cloneDeep(state.list));

      return {
        ...state,
        list,
      };
    }),
    on(MarketComparisonActions.ClearAllGroundTruthSuccess, (state, action): TopicState => {
      const list = cloneDeep(state.list);
      const column = list[action.event.columnId] || {};

      for (const transactionId of action.event.transactionIds) {
        const datapoint = column[transactionId] as Partial<Datapoint>;
        if (datapoint?.datapointHash === action.event.datapointHash) {
          delete column[transactionId];
        }
      }

      list[action.event.columnId] = column;
      return { ...state, list };
    }),
    on(
      TopicActions.LoadTopicValuesFail,
      (state, action): TopicState => ({
        ...state,
        error: action.error,
        loadingColumns: { ...state.loadingColumns, ...action.finishedColumns },
      })
    ),
    on(
      TopicActions.SaveTopic,
      (state, action): TopicState => ({
        ...state,
        columns: action.columns || state.columns,
        submitting: true,
      })
    ),
    on(
      TopicActions.SaveTopicSuccess,
      (state, action): TopicState => ({
        ...state,
        submitting: false,
      })
    ),
    on(
      TopicActions.SaveTopicFail,
      (state, action): TopicState => ({
        ...state,
        submitting: false,
        error: action.error,
      })
    ),
    on(
      TopicActions.SetRowHeight,
      (state, action): TopicState => ({
        ...state,
        rowHeight: action.rowHeight || state.rowHeight,
      })
    ),
    on(
      TopicActions.SetFrequentEntryHightlights,
      (state, action): TopicState => ({
        ...state,
        frequentEntryHightlights: action.frequentEntryHightlights,
      })
    ),
    on(
      TopicActions.ReRunSingleDatapointInitiated,
      (state, action): TopicState => ({
        ...state,
        list: {
          ...state.list,
          [action.columnId]: {
            ...state.list[action.columnId],
            [action.transactionId]: {
              value: loadingPlaceholder,
            },
          },
        },
      })
    )
  )
);

export function reducer(state: TopicState | undefined, action: Action) {
  return featureReducer(state, action);
}
